import { Settings, X, Minimize2, Maximize2, Check, XCircle, Clock } from 'lucide-react'
import { QUANTUM_COLORS } from './constants.js'

// Badge del dashboard: tarjeta paper con header arrastrable (molde
// patron_ruptura_quantum/Badge.jsx). Toda lectura de `result` va con
// guardas: el badge nunca debe lanzar con result null/undefined/parcial.
//
// SIN sección de historial/winrate (WP §3, decisión cerrada): el motor ni
// siquiera calcula ganadas/perdidas -- no hay nada de eso que mostrar. El
// dashboard es de ESTADO DEL DÍA (rango/EMA/acumulación/operaciones hoy),
// como pide WP §5.

function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function formatTime(t) {
  if (t == null) return '—'
  if (typeof t === 'number' && Number.isFinite(t)) {
    const d = new Date(t * 1000)
    return Number.isNaN(d.getTime()) ? String(t) : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }
  return String(t)
}

const CLOSE_REASON_LABEL = {
  SL: 'SL',
  TP: 'TP',
  Cierre: 'Cierre por tiempo',
  Giro: 'Giro',
  reset_newA: 'Descartada (reset)',
}

function statusLabel(sig) {
  if (!sig) return '—'
  if (sig.status === 'active') return 'Abierta'
  return CLOSE_REASON_LABEL[sig.closeReason] ?? sig.closeReason ?? '—'
}

// Icono por resultado en "Últimas N" (micro-WP aditivo, 2026-09-23): el
// dato (closeReason) ya llega del backend en cada señal, sin cambios de
// API. Solo TP/SL/Cierre (tiempo) tienen icono -- Giro (estructuralmente
// inalcanzable, ver docstring del motor) y reset_newA (descartada, no
// cuenta como resultado real) se quedan sin icono, texto solo, igual que
// antes.

// R firmado por señal (micro-WP badge R, 2026-09-24): TP/SL ya tienen R
// implícito por el propio icono (+rr / -1, ver docs/indicators/
// nasdaq_quantum.md) -- transparencia real hacía falta solo para "Cierre
// por tiempo" (y cualquier motivo futuro no-TP/SL), que hoy se lee como un
// resultado neutro cuando en la ventana de 90 días fueron unas veces
// pérdida pequeña y otras ganancia pequeña, sin forma de distinguirlas a
// simple vista. Cero endpoint backend que tocar: el dashboard NASDAQ es
// 100% cliente (motor de presentación, ver ficha §7 "no se persiste -- se
// recalcula desde las velas cargadas"), y entry/sl/closePx ya llegan en
// cada señal vía adapter.js -- confirmado antes de escribir esto, no
// asumido. El cálculo vive aquí (presentación), no en adapter.js, que
// declara explícitamente "CERO lógica de cálculo propia".
//
// R = (closePx - entry) / (entry - sl) -- la MISMA expresión sirve para
// LONG y SHORT sin rama por dirección: para un short, tanto el numerador
// como el denominador cambian de signo a la vez (entry-sl < 0 porque
// sl > entry), así que el cociente ya sale con el signo correcto.
// Verificado contra los R reales publicados en docs/research/
// nasdaq-quantum-frozen-window-90d-sweep-2026-09-23/README.md (p.ej. señal
// #4, SHORT, Cierre: entry=59432.99 sl=60031.75 close=59319.99 ->
// (59319.99-59432.99)/(59432.99-60031.75) = +0.1887 ≈ +0.189 publicado).
function rMultiple(sig) {
  if (!sig || sig.closeReason === 'TP' || sig.closeReason === 'SL') return null
  if (!Number.isFinite(sig.entry) || !Number.isFinite(sig.sl) || !Number.isFinite(sig.closePx)) return null
  const risk = sig.entry - sig.sl
  if (risk === 0) return null
  return (sig.closePx - sig.entry) / risk
}

function formatRMultiple(r) {
  if (!Number.isFinite(r)) return null
  const sign = r > 0 ? '+' : r < 0 ? '-' : ''
  return `${sign}${Math.abs(r).toFixed(1)} R`
}

function rColor(r) {
  if (!Number.isFinite(r) || r === 0) return 'var(--ink-soft, #4A5A52)'
  return r > 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
}

function ResultIcon({ closeReason, r }) {
  if (closeReason === 'TP') return <Check size={9} style={{ color: QUANTUM_COLORS.bull }} aria-label="TP" data-result-icon="TP" />
  if (closeReason === 'SL') return <XCircle size={9} style={{ color: QUANTUM_COLORS.bear }} aria-label="SL" data-result-icon="SL" />
  if (closeReason === 'Cierre') return <Clock size={9} style={{ color: rColor(r) }} aria-label="Cierre por tiempo" data-result-icon="Cierre" />
  return null
}

function Row({ label, value, color }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[9px] shrink-0 whitespace-nowrap" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{label}</span>
      <span className="text-[9px] font-medium" style={{ color: color ?? 'var(--ink, #15211B)' }}>{value}</span>
    </div>
  )
}

export default function NasdaqQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '0.1.0',
  onHeaderMouseDown,
}) {
  const res = result && typeof result === 'object' ? result : null
  const dash = res?.dashboard ?? null
  const active = res?.activeSignal ?? null
  const last = res?.lastSignal ?? null
  const gated = res?.gated === true || res == null

  const rangeLabel = dash?.rangeState === 'ready' ? 'Listo'
    : dash?.rangeState === 'forming' ? 'Formándose'
      : 'Esperando sesión'
  const rangeColor = dash?.rangeState === 'ready' ? QUANTUM_COLORS.bull : 'var(--ink-soft, #4A5A52)'

  const stateLabel = active ? `${active.direction} activa` : gated ? 'Inactivo' : rangeLabel
  const stateColor = active
    ? (active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear)
    : rangeColor

  const showLastSignals = res?.showLastSignals !== false
  const historyList = Array.isArray(res?.signals) ? [...res.signals].reverse().slice(0, 10) : []

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          NASDAQ Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: stateColor }}>· {stateLabel}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {typeof onMinimize === 'function' && (minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        ))}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Estado</span>
              <span className="text-[11px] font-semibold" style={{ color: stateColor }}>{stateLabel}</span>
            </div>
          </div>

          {dash && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 grid grid-cols-2 gap-x-2 gap-y-1">
                <Row label="Rango" value={dash.rangeHigh != null ? `${formatPrice(dash.rangeLow)} – ${formatPrice(dash.rangeHigh)}` : '—'} />
                <Row
                  label="EMA"
                  value={dash.emaSeparated == null ? '—' : (dash.emaSeparated ? 'Separado ✓' : 'Pegado ✗')}
                  color={dash.emaSeparated === false ? QUANTUM_COLORS.bear : QUANTUM_COLORS.bull}
                />
                <Row
                  label="Acumulación"
                  value={dash.accumulation == null ? '—' : (dash.accumulation ? 'Sí ✗' : 'No ✓')}
                  color={dash.accumulation ? QUANTUM_COLORS.bear : QUANTUM_COLORS.bull}
                />
                <Row label="Operaciones hoy" value={`${dash.tradesToday ?? 0} / ${dash.maxTrades ?? '—'}`} />
              </div>
            </div>
          )}

          {active && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Señal activa</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <Row label="Dirección" value={active.direction} color={active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                <Row label="Entrada" value={formatPrice(active.entry)} />
                <Row label="SL" value={formatPrice(active.sl)} />
                <Row label="TP" value={formatPrice(active.tp)} />
              </div>
            </div>
          )}

          {!active && last && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Última señal</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <Row label="Dirección" value={last.direction} color={last.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                <Row label="Resultado" value={statusLabel(last)} />
                <Row label="Entrada" value={formatPrice(last.entry)} />
                <Row label="Cierre" value={formatPrice(last.closePx)} />
                <div className="col-span-2">
                  <Row label="Fecha" value={formatTime(last.time)} />
                </div>
              </div>
            </div>
          )}

          {showLastSignals && historyList.length > 0 && (
            <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Últimas {historyList.length}</span>
              </div>
              <div className="flex flex-col gap-0.5 max-h-[160px] overflow-y-auto">
                {historyList.map(sig => {
                  const r = rMultiple(sig)
                  const rText = formatRMultiple(r)
                  return (
                    <div key={sig.id} className="flex items-center justify-between gap-1">
                      <span className="text-[8px] whitespace-nowrap" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                        {formatTime(sig.time)}
                      </span>
                      <span className="text-[8px] font-medium whitespace-nowrap" style={{ color: sig.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                        {sig.direction} {formatPrice(sig.entry)}
                      </span>
                      <span className="flex items-center gap-0.5 text-[8px] whitespace-nowrap" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                        <ResultIcon closeReason={sig.closeReason} r={r} />
                        {statusLabel(sig)}{rText ? ` · ${rText}` : ''}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
